/**
 * LedgerTabs - 账本切换标签组件
 * 
 * 用于在公账和私账之间切换的标签栏组件，带有动画效果和徽章显示
 */
import React, { useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, Animated, StyleSheet, ActivityIndicator, Platform } from 'react-native';
import { MODULE_COLORS } from '@cubemeet/core';

export type LedgerTabType = 'public' | 'private';

export interface LedgerTabsProps {
  /**
   * 当前激活的标签
   */
  activeTab: LedgerTabType;
  /**
   * 标签切换回调
   */
  onTabChange: (tab: LedgerTabType) => void;
  /**
   * 公账收据数量
   */
  publicCount: number;
  /**
   * 私账收据数量
   */
  privateCount: number;
  /**
   * 私账是否正在加载
   */
  privateLoading?: boolean;
  /**
   * 翻译函数
   */
  t?: (key: string) => string;
}

const defaultT = (key: string) => {
  const translations: Record<string, string> = {
    'ledgerTabs.public': '群组账本',
    'ledgerTabs.private': '成员私账',
  };
  return translations[key] || key;
};

/**
 * LedgerTabs 组件
 */
const LedgerTabs: React.FC<LedgerTabsProps> = ({
  activeTab,
  onTabChange,
  publicCount,
  privateCount,
  privateLoading = false,
  t = defaultT,
}) => {
  // Debug: log incoming props so devs can inspect values in browser console
  React.useEffect(() => {
  }, [activeTab, publicCount, privateCount, privateLoading]);
  const [cardWidth, setCardWidth] = React.useState(0);
  const ledgerAnimation = useRef(new Animated.Value(activeTab === 'public' ? 0 : 1)).current;

  useEffect(() => {
    Animated.timing(ledgerAnimation, {
      toValue: activeTab === 'public' ? 0 : 1,
      duration: 220,
      useNativeDriver: false, // Web 环境不支持 useNativeDriver
    }).start();
  }, [activeTab, ledgerAnimation]);

  const tabIndicatorTranslateX = cardWidth > 0
    ? ledgerAnimation.interpolate({
        inputRange: [0, 1],
        outputRange: [0, cardWidth / 2],
      })
    : 0;

  return (
    <View
      style={styles.wrapper}
      onLayout={({ nativeEvent }) => {
        const width = nativeEvent.layout.width;
        if (width && Math.abs(width - cardWidth) > 1) {
          setCardWidth(width);
        }
      }}
    >
      {cardWidth > 0 && (
        <Animated.View
          style={[
            styles.indicator,
            { pointerEvents: 'none' as any },
            {
              width: cardWidth / 2,
              transform: [{ translateX: tabIndicatorTranslateX }],
            },
          ]}
        />
      )}

      {/* 公账标签 */}
      <TouchableOpacity
        style={styles.tab}
        onPress={() => onTabChange('public')}
        activeOpacity={0.8}
      >
        <Text style={[styles.tabText, activeTab === 'public' && styles.tabTextActive]}>
          {t('ledgerTabs.public')}
        </Text>
        <View style={[styles.badge, activeTab === 'public' && styles.badgeActive]}>
          <Text style={[styles.badgeText, activeTab === 'public' && styles.badgeTextActive]}>
            {publicCount}
          </Text>
        </View>
      </TouchableOpacity>

      {/* 私账标签 */}
      <TouchableOpacity
        style={styles.tab}
        onPress={() => onTabChange('private')}
        activeOpacity={0.8}
      >
        <Text style={[styles.tabText, activeTab === 'private' && styles.tabTextActive]}>
          {t('ledgerTabs.private')}
        </Text>
        <View style={[styles.badge, activeTab === 'private' && styles.badgeActive]}>
          {privateLoading && activeTab === 'private' ? (
            <ActivityIndicator
              size="small"
              color={activeTab === 'private' ? '#fff' : '#475569'}
              style={styles.badgeLoader}
            />
          ) : (
            <Text style={[styles.badgeText, activeTab === 'private' && styles.badgeTextActive]}>
              {privateCount}
            </Text>
          )}
        </View>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'row',
    backgroundColor: '#f1f5f9',
    borderRadius: 12,
    padding: 4,
    marginBottom: 16,
    position: 'relative',
  },
  indicator: {
    position: 'absolute',
    top: 4,
    left: 4,
    bottom: 4,
    backgroundColor: '#fff',
    borderRadius: 10,
    // Use boxShadow on web; keep native shadow props for other platforms
    ...Platform.select({
      web: {
        boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
      },
    }),
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    gap: 8,
  },
  tabText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#64748b',
  },
  tabTextActive: {
    color: MODULE_COLORS.travel,
  },
  badge: {
    minWidth: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#e2e8f0',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  badgeActive: {
    backgroundColor: MODULE_COLORS.travel,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#475569',
  },
  badgeTextActive: {
    color: '#fff',
  },
  badgeLoader: {
    width: 16,
    height: 16,
  },
});

export default LedgerTabs;
